import { ErrorMessage, Field, Form, Formik, useFormikContext } from 'formik';
import { Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import type { FieldProps } from 'formik';
import { array, number, object, string } from 'yup';

import { createRoute, PATHS } from '@core/router/paths';
import { Button } from '@design-system/button';
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@design-system/dialog';
import { useDialogContext } from '@design-system/dialog/use-dialog-context';
import { Label } from '@design-system/label';
import MultipleSelector from '@design-system/multi-select';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@design-system/select';
import { CustomErrorMessage } from '@design-system/error-message';
import { Textfield } from '@shared/fields/text-field';
import { TiptapEditor } from '@shared/components/reusables/tiptap-editor';
import { knowledgeBaseApi } from '@features/knowledge-base/services/knowledge-base-api';
import { useChannels } from '@features/settings/pages/integration/services/channels-api';

import {
  agentFieldErrors,
  useCreateAgent,
  useUpdateAgent,
  type AgentInput,
  type AgentTone,
  type AIAgent,
} from '../services/agents-api';
import { agentChannels, agentKbases } from '../utils/labels';

const TONES: { value: AgentTone; label: string }[] = [
  { value: 'FRIENDLY', label: 'Friendly' },
  { value: 'PROFESSIONAL', label: 'Formal' },
  { value: 'CONCISE', label: 'Concise' },
];

type FormValues = {
  name: string;
  instructions: string;
  tone: AgentTone;
  kbase_ids: number[];
  channel_ids: number[];
};

const schema = object({
  name: string()
    .trim()
    .required('Name is required')
    .max(60, 'At most 60 characters'),
  instructions: string().max(4096, 'At most 4096 characters'),
  tone: string().oneOf(TONES.map((tone) => tone.value)),
  // The backend refuses an agent with no knowledge base (kbase_ids may not be empty): it is what the
  // agent answers from. Said here, in words, before the server answers with a raw field key.
  kbase_ids: array()
    .of(number().integer())
    .min(1, 'Pick at least one knowledge base: the agent answers from it.'),
  channel_ids: array().of(number().integer()),
});

function ToneField() {
  const { values, setFieldValue } = useFormikContext<FormValues>();
  return (
    <div>
      <Label htmlFor='tone'>Tone</Label>
      <Select
        value={values.tone}
        onValueChange={(value) => setFieldValue('tone', value)}
      >
        <SelectTrigger
          id='tone'
          className='bg-primary-30 border-primary-200 h-12'
        >
          <SelectValue placeholder='Select a tone' />
        </SelectTrigger>
        <SelectContent>
          {TONES.map((tone) => (
            <SelectItem
              key={tone.value}
              value={tone.value}
            >
              {tone.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <ErrorMessage
        name='tone'
        component={CustomErrorMessage}
      />
    </div>
  );
}

function KbasesField() {
  const { values, setFieldValue } = useFormikContext<FormValues>();
  const list = knowledgeBaseApi.useFetch({ page_size: 100 });
  const options = (list.data?.results ?? []).map((kbase) => ({
    value: String(kbase.id),
    label: kbase.name,
  }));
  const selected = options.filter((option) =>
    values.kbase_ids.includes(Number(option.value))
  );

  if (!list.isPending && options.length === 0) {
    return (
      <div>
        <Label htmlFor='kbase_ids'>General Knowledge Base</Label>
        <p className='text-sm text-gray-700'>
          No knowledge base yet. An agent answers from one, so create it first.{' '}
          <Link
            to={createRoute(PATHS.CONVERSATIONS_KNOWLEDGE_BASE)}
            className='font-medium text-primary-1000 underline'
          >
            Create a knowledge base
          </Link>
        </p>
        <ErrorMessage
          name='kbase_ids'
          component={CustomErrorMessage}
        />
      </div>
    );
  }

  return (
    <div>
      <Label htmlFor='kbase_ids'>General Knowledge Base</Label>
      <MultipleSelector
        value={selected}
        options={options}
        placeholder={list.isPending ? 'Loading…' : 'Select knowledge bases'}
        emptyIndicator={
          <p className='p-2 text-center text-sm text-gray-500'>
            No more knowledge bases to add.
          </p>
        }
        hidePlaceholderWhenSelected
        commandProps={{ label: 'Knowledge bases', id: 'kbase_ids' }}
        onChange={(picked) =>
          setFieldValue(
            'kbase_ids',
            picked.map((option) => Number(option.value))
          )
        }
      />
      <ErrorMessage
        name='kbase_ids'
        component={CustomErrorMessage}
      />
      <p className='mt-1.5 text-xs text-gray-600'>
        The agent answers from these.
      </p>
    </div>
  );
}

function ChannelsField() {
  const { values, setFieldValue } = useFormikContext<FormValues>();
  const { data: channels = [], isPending } = useChannels();
  const options = channels.map((channel) => ({
    value: String(channel.id),
    label: channel.display_name || channel.external_id,
  }));
  const selected = options.filter((option) =>
    values.channel_ids.includes(Number(option.value))
  );

  return (
    <div>
      <Label htmlFor='channel_ids'>Channels</Label>
      <MultipleSelector
        value={selected}
        options={options}
        placeholder={isPending ? 'Loading…' : 'Select channels'}
        emptyIndicator={
          channels.length === 0 ? (
            <p className='p-2 text-center text-sm text-gray-500'>
              No channels connected yet.{' '}
              <Link
                to={createRoute(PATHS.CONVERSATIONS_CHANNELS)}
                className='font-medium text-primary-1000 underline'
              >
                Connect a channel
              </Link>
            </p>
          ) : (
            <p className='p-2 text-center text-sm text-gray-500'>
              No more channels to add.
            </p>
          )
        }
        hidePlaceholderWhenSelected
        commandProps={{ label: 'Channels', id: 'channel_ids' }}
        onChange={(picked) =>
          setFieldValue(
            'channel_ids',
            picked.map((option) => Number(option.value))
          )
        }
      />
      <ErrorMessage
        name='channel_ids'
        component={CustomErrorMessage}
      />
      <p className='mt-1.5 text-xs text-gray-600'>You can do this later</p>
    </div>
  );
}

function AgentForm({
  agent,
  onDone,
}: {
  agent: AIAgent | null;
  onDone: (saved: AIAgent) => void;
}) {
  const { formikRef } = useDialogContext();
  const create = useCreateAgent({ inlineFieldErrors: true });
  const update = useUpdateAgent({ inlineFieldErrors: true });
  const isPending = create.isPending || update.isPending;

  return (
    <Formik<FormValues>
      innerRef={formikRef}
      enableReinitialize
      validationSchema={schema}
      initialValues={{
        name: agent?.name ?? '',
        instructions: agent?.instructions ?? '',
        tone: agent?.tone ?? 'FRIENDLY',
        kbase_ids: agent ? agentKbases(agent).map((kbase) => kbase.id) : [],
        channel_ids: agent
          ? agentChannels(agent).map((channel) => channel.id)
          : [],
      }}
      onSubmit={async (values, { setErrors }) => {
        const payload: AgentInput = {
          name: values.name.trim(),
          instructions: values.instructions,
          tone: values.tone,
          kbase_ids: values.kbase_ids,
          channel_ids: values.channel_ids,
        };
        try {
          const saved = agent
            ? await update.mutateAsync({ id: agent.id, ...payload })
            : await create.mutateAsync(payload);
          onDone(saved);
        } catch (error) {
          // A refused field is shown under that field ("An agent with this name
          // already exists."); one this form has no field for is a toast.
          const fields = agentFieldErrors(error);
          if (!fields) return;
          const shown = Object.fromEntries(
            Object.entries(fields).filter(([key]) => key in values)
          );
          setErrors(shown);
          Object.entries(fields)
            .filter(([key]) => !(key in values))
            .forEach(([key, message]) => toast.error(`${key}: ${message}`));
        }
      }}
    >
      {({ submitForm }) => (
        <Form className='flex min-h-0 flex-1 flex-col'>
          <DialogBody className='flex flex-col gap-4 p-4!'>
            <section className='flex flex-col gap-4 rounded-xl border border-pale-border p-4'>
              <p className='text-sm font-medium text-gray-600'>1. Agent Info</p>
              <Textfield
                name='name'
                label='Agent Name'
                placeholder='Enter agent name'
              />
              <ToneField />
              <Field name='instructions'>
                {({ field, form, meta }: FieldProps<string>) => (
                  <div>
                    <TiptapEditor
                      label='Instructions'
                      labelProps={{ className: 'mb-0' }}
                      value={field.value}
                      onChange={(html) => form.setFieldValue(field.name, html)}
                      placeholder='e.g. You are a friendly admissions assistant. Answer only from the knowledge base…'
                      contentClassName='[&_.ProseMirror]:!min-h-[140px]'
                      error={!!meta.error && meta.touched}
                      errorText={
                        meta.touched && meta.error ? meta.error : undefined
                      }
                    />
                    <p className='mt-1.5 text-xs text-gray-600'>
                      Describe the AI Agent’s role, communication style, and the
                      actions it should perform. Give step-by-step instructions
                      using clear, actionable language.
                    </p>
                  </div>
                )}
              </Field>
            </section>
            <section className='flex flex-col gap-4 rounded-xl border border-pale-border p-4'>
              <p className='text-sm font-medium text-gray-600'>
                2. Knowledge Base
              </p>
              <KbasesField />
            </section>
            <section className='flex flex-col gap-4 rounded-xl border border-pale-border p-4'>
              <p className='text-sm font-medium text-gray-600'>
                3. Connected Channels
              </p>
              <ChannelsField />
            </section>
          </DialogBody>
          <DialogFooter>
            <Button
              type='button'
              variant='primary'
              className='flex-1'
              isLoading={isPending}
              onClick={submitForm}
            >
              Confirm
            </Button>
            <DialogClose asChild>
              <Button
                type='button'
                variant='primaryOutline'
                className='flex-1 border-pale-border text-gray-800'
              >
                Cancel
              </Button>
            </DialogClose>
          </DialogFooter>
        </Form>
      )}
    </Formik>
  );
}

export function AgentCreateDialog({
  agent,
  open,
  onOpenChange,
  onSaved,
}: {
  agent: AIAgent | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved?: (saved: AIAgent) => void;
}) {
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
    >
      <DialogContent
        align='right'
        className='max-w-[600px] rounded-2xl'
      >
        <DialogHeader>
          <DialogTitle>{agent ? 'Edit Agent' : 'New Agent'}</DialogTitle>
        </DialogHeader>
        <AgentForm
          agent={agent}
          onDone={(saved) => {
            onOpenChange(false);
            onSaved?.(saved);
          }}
        />
      </DialogContent>
    </Dialog>
  );
}
